import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { allowsUnmarriedPartner, Borrower } from "../../models/borrower-model";
import { ConfigurableFieldHostComponent } from "../configurable-field-host.component";
import { formViewProvider } from "../../services/form-view.provider";
import { v4 as uuidv4 } from 'uuid';
import { EnumerationItem } from "projects/shared/models/enumeration-item.model";
import { EnumsService } from "../../services/enums.service";
import { DomesticRelationshipTypeEnum } from "../../models/mortgage.model";

@Component({
    selector: 'borrower-info-mini',
    templateUrl: 'borrower-info-mini.component.html',
    viewProviders: [formViewProvider],
    standalone: false
})
export class BorrowerInfoMiniComponent extends ConfigurableFieldHostComponent implements OnInit {

  @ViewChild('borrowerInfoForm') borrowerInfoForm: NgForm | undefined;

  @Output()
  borrowerMarkedAsMarriedToPrimaryBorrower = new EventEmitter<Borrower>();

  @Input()
  customMessage: string | undefined;

  @Input()
  borrower!: Borrower;

  @Input()
  restrictMiddleNameToSingleCharacter: boolean = true;

  // LLS-255: when false, the co-borrower's sensitive personal fields are shown only if the
  // primary borrower affirms they'll complete the application for this person. Default true
  // preserves existing behavior.
  @Input()
  allowBorrowerEnterCoBorrowerPersonalInformation: boolean = true;

  @Input()
  set borrowerSettings(borrowerSettings: Map<number, boolean> | undefined) {
    const settings = borrowerSettings?.get(this.borrower.borrowerId);
    if (settings != null) {
      this.mainBorrowerWillApplyForMe = settings;
    }
    this._borrowerSettings = borrowerSettings;
  }

  mainBorrowerWillApplyForMe: boolean | null = null;

  mainBorrowerIsMarriedToCoBorrower: boolean | null = null;

  ssnPattern = { '0': { pattern: new RegExp('\\d'), symbol: 'X' } };

  private _borrowerSettings: Map<number, boolean> | undefined = undefined;

  protected isMobile: boolean = false;

  protected componentId: string = uuidv4();

  protected get languagePreferenceOptions(): EnumerationItem[] {
    return this._enumService.languages;
  }

  // WI 24451 — unmarried-partner gate + options. LOCAL/derived UI flag (no persisted boolean on
  // MortgageBorrower; reconstructed from the relationship fields on load — mirrors URLA). Known limitation:
  // a bare "yes" with no Type/State entered isn't retained on reload, by design (persisted data is Type/State).
  protected unmarriedAddendum: boolean = false;
  protected readonly DomesticRelationshipTypeEnum = DomesticRelationshipTypeEnum;
  protected get domesticRelationshipTypeOptions() { return this._enumService.domesticRelationshipTypes; }
  protected get usStates() { return this._enumService.states; }

  /**
   * LLS-255: sensitive co-borrower fields hide unless the borrower affirms they'll complete the
   * application for this person. Purely additive — it can only add a reason to hide, never overrides
   * the admin's field config or changes a shown field's required state.
   */
  protected get hideCoBorrowerPrivateFields(): boolean {
    return !this.allowBorrowerEnterCoBorrowerPersonalInformation
      && this.mainBorrowerWillApplyForMe !== true;
  }

  /** The gate (and group) shows only for an unmarried borrower and when an admin hasn't hidden it. */
  protected get showUnmarriedPartnerGate(): boolean {
    return allowsUnmarriedPartner(this.borrower?.maritalStatus)
      && !this.isHidden('borrower.unmarriedAddendum')
      && !this.hideCoBorrowerPrivateFields;
  }
  /** Type/State/Specify show only once the borrower confirms they have an unmarried partner. */
  protected get showUnmarriedPartnerDetails(): boolean {
    return this.showUnmarriedPartnerGate && this.unmarriedAddendum;
  }

  constructor(private readonly _enumService: EnumsService) {
    super();
  }

  ngOnInit(): void {
    this.configureBasedOnScreenSize(window.innerWidth);
    this.unmarriedAddendum = allowsUnmarriedPartner(this.borrower.maritalStatus)
      && !!(this.borrower.domesticRelationshipType
            || this.borrower.unmarriedRelationshipState
            || this.borrower.unmarriedRelationshipTypeOther);
  }

  onMiddleNameKeyPress(event: KeyboardEvent): void {
    const char = String.fromCharCode(event.keyCode);
    const pattern = /^[a-zA-Z]$/;

    if (!pattern.test(char)) {
      event.preventDefault();
    }
  }
  onLanguagePreferenceChanged = () => {
    if (this.borrower.languagePreference !== 'Other') {
      this.borrower.languagePreferenceOtherDescription = null;
    }
  }

  // WI 24451 — cleanup cascade (mirrors My Info / URLA contact-info).
  onMaritalStatusChanged = () => {
    if (!allowsUnmarriedPartner(this.borrower.maritalStatus)) {
      this.unmarriedAddendum = false;
      this.onUnmarriedAddendumChanged();
    }
  }

  onUnmarriedAddendumChanged = () => {
    if (!this.unmarriedAddendum) {
      this.borrower.domesticRelationshipType = null;
      this.borrower.unmarriedRelationshipState = null;
      this.onDomesticRelationshipTypeChanged();
    }
  }

  onDomesticRelationshipTypeChanged = () => {
    if (this.borrower.domesticRelationshipType !== DomesticRelationshipTypeEnum.Other) {
      this.borrower.unmarriedRelationshipTypeOther = null;
    }
  }



  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.configureBasedOnScreenSize(window.innerWidth);
  }

  setBorrowerIdContactId = (borrowerId: number, contactId: number) => {
    this.borrower.borrowerId = borrowerId;
    this.borrower.contactId = contactId;
  }

  validate = () => {
    this.borrowerInfoForm?.form.markAllAsTouched();
    if (this.borrowerInfoForm?.form.valid) {
      return true;
    }
    return false;
  }

  scrollToFirstInvalid = () => {
    let firstInvalidOneId: string = null;
    for (const key in this.borrowerInfoForm.form.controls) {
      if (Object.prototype.hasOwnProperty.call(this.borrowerInfoForm.form.controls, key)) {
        if (this.borrowerInfoForm.form.controls[key].status === 'INVALID') {
          firstInvalidOneId = key;
          break;
        }
      }
    }
    if (firstInvalidOneId) {
      const elements = document.getElementsByName(firstInvalidOneId);
      let element;
      if (elements) {
        element = elements[0]
      }
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 250);
      }
    }
  }

  protected onMainBorrowerWillApplyForMeChanged = (checked: boolean) => {
    this._borrowerSettings?.set(this.borrower.borrowerId, checked);
    // LLS-255: under privacy mode, "No" means the borrower isn't entering the co-borrower's
    // private info — drop anything typed this session so it isn't submitted. Only on the explicit
    // "No" action (not on load/restore): a "No" co-borrower may legitimately carry their OWN data
    // (entered via their co-apply flow), which we must not wipe. The co-borrower provides it there.
    if (!this.allowBorrowerEnterCoBorrowerPersonalInformation && checked === false) {
      this.clearCoBorrowerPrivateFields();
    }
  }

  /** The sensitive co-borrower fields hidden by the privacy gate (kept in sync with the template). */
  private clearCoBorrowerPrivateFields(): void {
    this.borrower.maritalStatus = null;
    this.borrower.dateOfBirth = "";
    this.borrower.socialSecNum = "";
    if (this.borrower.declarations) {
      this.borrower.declarations.residenceStatus = null;
    }
    this.borrower.languagePreference = null;
    this.borrower.languagePreferenceOtherDescription = null;
    this.unmarriedAddendum = false;
    this.borrower.domesticRelationshipType = null;
    this.borrower.unmarriedRelationshipState = null;
    this.borrower.unmarriedRelationshipTypeOther = null;
  }

  protected onMainBorrowerIsMarriedToBorrowerChanged = (checked: boolean) => {
    if (checked) {
      this.borrowerMarkedAsMarriedToPrimaryBorrower.emit(this.borrower);
    }
  }

  private configureBasedOnScreenSize(windowWidth: number) {
    this.isMobile = windowWidth < 768
  }
}
